import React from 'react';
import { PLAYER_COLORS, DEFAULT_MAX_BOMBS, DEFAULT_EXPLOSION_RADIUS, DEFAULT_SPEED, CANVAS_WIDTH, CANVAS_HEIGHT } from '../shared/constants';

const PlayerStatsDisplay = ({ players, gameState, gameTimer, suddenDeath }) => {
  if (gameState !== 'playing') return null;

  // Format timer as MM:SS
  const formatTimer = (milliseconds) => {
    const totalSeconds = Math.max(0, Math.ceil(milliseconds / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Use a flexible width with better minimum size
  const minWidth = 480; // Ensure minimum width for proper layout
  const maxWidth = Math.min(600, window.innerWidth - 40); // Max 600px or available width

  // Create array of 8 player slots
  const playerSlots = Array(8).fill(null).map((_, index) => {
    const playerId = index;
    const player = Array.from(players.values()).find(p => p.playerId === playerId);
    return { playerId, player };
  });

  const renderUpgrades = (player) => {
    if (!player) return null;

    const upgrades = [];
    const bombCount = Math.max(0, (player.maxBombs || DEFAULT_MAX_BOMBS) - DEFAULT_MAX_BOMBS);
    const flameCount = Math.max(0, (player.explosionRadius || DEFAULT_EXPLOSION_RADIUS) - DEFAULT_EXPLOSION_RADIUS);
    const speedDifference = (player.speed || DEFAULT_SPEED) - DEFAULT_SPEED;
    // Speed upgrades might add 0.5 or other increments, so count any significant speed increase
    const speedCount = Math.max(0, Math.floor(speedDifference / 0.5)); // Each 0.5 speed increase = 1 upgrade icon
    

    // Add bomb upgrades
    for (let i = 0; i < bombCount; i++) {
      upgrades.push({
        type: 'bomb',
        sprite: '/sprites/upgrade-bomb.svg',
        key: `bomb-${i}`
      });
    }

    // Add flame upgrades
    for (let i = 0; i < flameCount; i++) {
      upgrades.push({
        type: 'flame',
        sprite: '/sprites/upgrade-flame.svg',
        key: `flame-${i}`
      });
    }

    // Add speed upgrades
    for (let i = 0; i < speedCount; i++) {
      upgrades.push({
        type: 'speed',
        sprite: '/sprites/upgrade-skate.svg',
        key: `speed-${i}`
      });
    }

    // Add kick upgrade
    if (player.canKick) {
      upgrades.push({
        type: 'kick',
        sprite: '/sprites/upgrade-kick.svg',
        key: 'kick'
      });
    }

    // Add spooge upgrade
    if (player.hasSpooge) {
      upgrades.push({
        type: 'spooge',
        sprite: '/sprites/item-spooge.svg',
        key: 'spooge'
      });
    }

    // Add hand upgrade
    if (player.hasHand) {
      upgrades.push({
        type: 'hand',
        sprite: '/sprites/upgrade-hand.svg',
        key: 'hand'
      });
    }

    // Add jelly upgrade
    if (player.hasJelly) {
      upgrades.push({
        type: 'jelly',
        sprite: '/sprites/upgrade-jelly.svg',
        key: 'jelly'
      });
    }

    // Add disease indicators
    if (player.diseases && Object.keys(player.diseases).length > 0) {
      const now = Date.now();
      
      // Check each disease type
      for (const [diseaseType, expiryTime] of Object.entries(player.diseases)) {
        if (now < expiryTime) {
          const remainingTime = Math.ceil((expiryTime - now) / 1000);
          upgrades.push({
            type: 'disease',
            sprite: '/sprites/upgrade-skull.svg',
            key: `disease-${diseaseType}`,
            diseaseType,
            remainingTime
          });
        }
      }
    }

    return upgrades.map((upgrade, index) => {
      if (upgrade.type === 'disease') {
        return (
          <div
            key={upgrade.key}
            style={{
              position: 'relative',
              display: 'inline-block',
              marginLeft: index > 0 ? '-4px' : '0px'
            }}
          >
            <img
              src={upgrade.sprite}
              alt={`${upgrade.diseaseType} disease`}
              style={{
                width: '16px',
                height: '16px',
                filter: 'drop-shadow(1px 1px 1px rgba(0,0,0,0.5))'
              }}
            />
            <div
              style={{
                position: 'absolute',
                bottom: '-2px',
                right: '-2px',
                backgroundColor: 'rgba(139, 0, 0, 0.9)',
                color: 'white',
                fontSize: '8px',
                fontWeight: 'bold',
                padding: '1px 2px',
                borderRadius: '2px',
                minWidth: '8px',
                textAlign: 'center',
                lineHeight: '1'
              }}
            >
              {upgrade.remainingTime}
            </div>
          </div>
        );
      }
      
      return (
        <img
          key={upgrade.key}
          src={upgrade.sprite}
          alt={upgrade.type}
          style={{
            width: '16px',
            height: '16px',
            marginLeft: index > 0 ? '-4px' : '0px', // Overlap slightly
            filter: 'drop-shadow(1px 1px 1px rgba(0,0,0,0.5))'
          }}
        />
      );
    });
  };

  const renderPlayerSlot = (slot, label) => {
    const { player } = slot;
    
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        minHeight: '20px',
        color: player ? PLAYER_COLORS[player.playerId] : '#666',
        fontSize: '14px',
        fontWeight: 'bold',
        opacity: player && !player.alive ? 0.6 : 1
      }}>
        <span style={{ marginRight: '8px', display: 'flex', alignItems: 'center', gap: '4px' }}>
          {player ? `${player.nickname || `Player ${player.playerId + 1}`}` : label}
          {player && !player.alive && (
            <span style={{ 
              fontSize: '12px',
              color: '#ff4444',
              textShadow: '0 0 2px rgba(255, 68, 68, 0.8)'
            }}>
              💀
            </span>
          )}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
          {renderUpgrades(player)}
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Add CSS animation for pulse effect */}
      {suddenDeath && (
        <style>
          {`
            @keyframes pulse {
              0% { opacity: 1; }
              50% { opacity: 0.5; }
              100% { opacity: 1; }
            }
          `}
        </style>
      )}
      
      <div style={{
        minWidth: `${minWidth}px`,
        maxWidth: `${maxWidth}px`,
        width: 'auto',
        margin: '0 auto',
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        border: '1px solid #444',
        borderRadius: '4px',
        padding: '6px 10px'
      }}>
        {/* Game Timer */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        marginBottom: '6px',
        paddingBottom: '6px',
        borderBottom: '1px solid #444'
      }}>
        {suddenDeath ? (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <span style={{
              color: '#ff0000',
              fontSize: '14px',
              fontWeight: 'bold',
              animation: 'pulse 1s infinite',
              textShadow: '0 0 8px rgba(255, 0, 0, 0.8)'
            }}>
              ⚠️ SUDDEN DEATH ⚠️
            </span>
          </div>
        ) : (
          <span style={{
            color: gameTimer < 30000 ? '#ff4444' : '#ffffff', // Red when under 30 seconds
            fontSize: '16px',
            fontWeight: 'bold',
            fontFamily: 'monospace'
          }}>
            {formatTimer(gameTimer)}
          </span>
        )}
      </div>
      
      {/* Player Stats Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '3px 10px'
      }}>
        {playerSlots.map((slot, index) => {
          // Only render slots that have players
          if (!slot.player) return null;
          return (
            <div key={slot.playerId}>
              {renderPlayerSlot(slot, `Player ${slot.playerId + 1}`)}
            </div>
          );
        })}
      </div>
      </div>
    </>
  );
};

export default PlayerStatsDisplay;